import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canBrowse } from "@/lib/constants";
import { Header } from "@/components/Header";
import { MapView } from "@/components/MapView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { MapPin, Star, Navigation, Route, Search, X, Sparkles, List, Map as MapIcon, Trash2, Heart, Lock, Tag, EyeOff, Award, GripVertical, Bookmark, ChevronDown, Gift, Briefcase, Phone } from "lucide-react";
import { Reorder } from "framer-motion";
import { toast } from "sonner";

function haversine(a, b) {
  const R = 6371, dLat = ((b[0] - a[0]) * Math.PI) / 180, dLng = ((b[1] - a[1]) * Math.PI) / 180;
  const lat1 = (a[0] * Math.PI) / 180, lat2 = (b[0] * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

function orderRoute(start, points) {
  const remaining = [...points];
  const path = [];
  let cur = start;
  while (remaining.length) {
    let best = 0, bestD = Infinity;
    remaining.forEach((p, i) => { const d = haversine(cur, [p.lat, p.lng]); if (d < bestD) { bestD = d; best = i; } });
    const next = remaining.splice(best, 1)[0];
    path.push(next);
    cur = [next.lat, next.lng];
  }
  return path;
}

function StarRow({ value = 0 }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`w-3.5 h-3.5 ${n <= Math.round(value) ? "fill-ys-gold text-ys-gold" : "text-slate-300"}`} />
      ))}
    </div>
  );
}

function ListingCard({ l, onOpen, onRoute, inRoute, onSave, saved, onHide, topRated }) {
  const cover = l.photos?.[l.profile_photo_index] || l.photos?.[0];
  return (
    <div data-testid={`listing-card-${l.id}`}
      className={`rounded-2xl overflow-hidden hover:shadow-lg transition-all cursor-pointer ${l.highlighted ? "border-2 border-ys-gold ring-2 ring-ys-gold/60 shadow-lg shadow-amber-300/60 bg-gradient-to-br from-amber-50 to-white" : "bg-white border border-slate-200"}`}>
      <div className="relative h-36 bg-slate-100" onClick={() => onOpen(l)}>
        {cover ? <img src={fileUrl(cover)} alt={l.title} className="w-full h-full object-cover" /> :
          <div className="w-full h-full flex items-center justify-center text-slate-300"><MapPin className="w-8 h-8" /></div>}
        {l.highlighted && <Badge className="absolute top-2 left-2 bg-ys-gold text-white gap-1 shadow"><Sparkles className="w-3 h-3" /> Highlighted</Badge>}
        {topRated && <Badge className="absolute bottom-2 left-2 bg-emerald-600 text-white gap-1 shadow" data-testid={`top-rated-badge-${l.id}`}><Award className="w-3 h-3" /> Top rated</Badge>}
        <button onClick={(e) => { e.stopPropagation(); onSave(l); }} data-testid={`save-toggle-${l.id}`}
          className="absolute top-2 right-2 p-1.5 rounded-full bg-white/90 hover:bg-white shadow">
          <Heart className={`w-4 h-4 ${saved ? "fill-red-500 text-red-500" : "text-slate-500"}`} />
        </button>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display font-semibold leading-tight line-clamp-1" onClick={() => onOpen(l)}>{l.title}</h3>
        </div>
        <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />{l.city}{l.state ? `, ${l.state}` : ""} {l.zip}</p>
        {l.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2" data-testid={`card-tags-${l.id}`}>
            {l.tags.slice(0, 4).map((t) => <span key={t} className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">{t}</span>)}
          </div>
        )}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-1.5"><StarRow value={l.rating_avg} /><span className="text-xs text-slate-400">({l.rating_count})</span></div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" className="rounded-full h-8 w-8 p-0 text-slate-400" data-testid={`hide-toggle-${l.id}`} onClick={(e) => { e.stopPropagation(); onHide(l); }}><EyeOff className="w-3.5 h-3.5" /></Button>
            <Button size="sm" variant={inRoute ? "secondary" : "outline"} className="rounded-full h-8 text-xs gap-1"
              data-testid={`route-toggle-${l.id}`} onClick={(e) => { e.stopPropagation(); onRoute(l); }}>
              <Route className="w-3 h-3" /> {inRoute ? "In route" : "Add"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Browse() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  const [promo, setPromo] = useState(null);
  useEffect(() => { api.get("/promo").then(({ data }) => setPromo(data)).catch(() => {}); }, []);
  const savedIds = new Set(user?.saved_listings || []);
  const locked = !canBrowse(user, promo);
  const promptPlan = () => { toast.info(user ? "Pick a plan to open sales and use the map" : "Sign in and pick a plan to open sales"); };
  const toggleSave = async (l) => {
    if (!user) { navigate("/login"); return; }
    try { const { data } = await api.post(`/listings/${l.id}/save`); await refreshUser(); toast.success(data.saved ? "Saved — we'll remind you the morning it's live" : "Removed from saved"); }
    catch { toast.error("Could not save"); }
  };
  const toggleHide = async (l) => {
    if (!user) { navigate("/login"); return; }
    try { await api.post(`/listings/${l.id}/hide`); await refreshUser(); toast.success("Hidden — you won't see this sale"); }
    catch { toast.error("Could not hide"); }
  };
  const [listings, setListings] = useState([]);
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [q, setQ] = useState("");
  const [stateF, setStateF] = useState("");
  const [tagFilter, setTagFilter] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [sort, setSort] = useState("recent");
  const [selected, setSelected] = useState(null);
  const [markerPreview, setMarkerPreview] = useState(null);
  const [listSheet, setListSheet] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchNonce, setSearchNonce] = useState(0);
  const [cols, setCols] = useState(2);
  const pinchRef = useRef(0);
  const [fitTrigger, setFitTrigger] = useState(0);
  const [userLocation, setUserLocation] = useState(null);
  const [route, setRoute] = useState([]);
  const [view, setView] = useState(() => (typeof window !== "undefined" && window.innerWidth < 1024) ? "map" : "split");
  const toggleTag = (t) => setTagFilter((cur) => cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]);
  const applySearch = () => { setSearchNonce((n) => n + 1); setSearchOpen(false); setView("map"); };
  const clearSearch = () => { setQ(""); setCity(""); setStateF(""); setZip(""); setTagFilter([]); setSort("recent"); setSearchNonce((n) => n + 1); };
  const filterSummary = [q, city, stateF, zip].filter(Boolean).concat(tagFilter).slice(0, 2).join(" · ");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (city) params.city = city;
      if (stateF) params.state = stateF;
      if (zip) params.zip = zip;
      if (tagFilter.length) params.tags = tagFilter.join(",");
      if (sort && sort !== "nearest") params.sort = sort;
      const { data } = await api.get("/listings", { params });
      setListings(data);
    } catch { toast.error("Could not load sales"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort, tagFilter, searchNonce]);
  useEffect(() => { api.get("/tags").then(({ data }) => setTagOptions(data.all)).catch(() => {}); }, []);

  const enableGps = () => {
    if (!navigator.geolocation) { toast.error("GPS not supported on this device"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setUserLocation([pos.coords.latitude, pos.coords.longitude]); toast.success("Showing sales near you — highlighted ones first"); },
      () => toast.error("Could not get your location"),
      { enableHighAccuracy: true }
    );
  };

  const changeSort = (v) => {
    setSort(v);
    if (v === "nearest" && !userLocation) {
      if (!navigator.geolocation) { toast.error("GPS not supported — can't sort by distance"); return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => { setUserLocation([pos.coords.latitude, pos.coords.longitude]); toast.success("Sorted by distance — nearest sales first"); },
        () => toast.error("Turn on location to sort by nearest"),
        { enableHighAccuracy: true }
      );
    }
  };

  const toggleRoute = (l) => {
    setRoute((prev) => prev.find((x) => x.id === l.id) ? prev.filter((x) => x.id !== l.id) : [...prev, l]);
  };

  const [manualOrder, setManualOrder] = useState(false);
  const orderedRoute = useMemo(() => {
    if (route.length === 0) return [];
    if (manualOrder) return route;
    const start = userLocation || [route[0].lat, route[0].lng];
    return orderRoute(start, route);
  }, [route, userLocation, manualOrder]);

  useEffect(() => {
    if (location.state?.loadRoute?.length) {
      setRoute(location.state.loadRoute);
      setManualOrder(true);
      toast.success("Loaded your saved route");
      window.history.replaceState({}, "");
    }
    /* eslint-disable-next-line */
  }, [location.state]);

  // Fit all sales into view when Browse opens on the map
  useEffect(() => { if (!loading && sorted.length) setFitTrigger((n) => n + 1); /* eslint-disable-next-line */ }, [loading]);

  // Browse always opens on the live map first (mobile); reset on every navigation here
  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 1024) { setView("map"); setSearchOpen(false); }
  }, [location.key]);

  const saveRoute = async () => {
    if (!user) { navigate("/login"); return; }
    const name = window.prompt("Name this route (e.g. 'Saturday Bend loop'):");
    if (name === null) return;
    const stops = orderedRoute.map((l) => ({ id: l.id, title: l.title, lat: l.lat, lng: l.lng, city: l.city, zip: l.zip }));
    try { await api.post("/my/routes", { name, stops }); toast.success("Route saved — find it in your profile"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const routePoints = useMemo(() => {
    if (orderedRoute.length === 0) return [];
    const pts = orderedRoute.map((l) => [l.lat, l.lng]);
    return userLocation ? [userLocation, ...pts] : pts;
  }, [orderedRoute, userLocation]);

  const [roadRoute, setRoadRoute] = useState(null);
  useEffect(() => {
    if (routePoints.length < 2) { setRoadRoute(null); return; }
    const coords = routePoints.map(([la, ln]) => `${ln},${la}`).join(";");
    let cancelled = false;
    fetch(`https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled || !d.routes?.length) { if (!cancelled) setRoadRoute(null); return; }
        const r0 = d.routes[0];
        setRoadRoute({ line: r0.geometry.coordinates.map(([ln, la]) => [la, ln]), distance: r0.distance, duration: r0.duration });
      })
      .catch(() => { if (!cancelled) setRoadRoute(null); });
    return () => { cancelled = true; };
  }, [routePoints]);

  const routeLine = roadRoute?.line || (routePoints.length > 1 ? routePoints : null);

  const startNavigation = () => {
    if (routePoints.length < 2) { toast.info("Add at least one stop to navigate"); return; }
    const stops = orderedRoute.map((l) => `${l.lat},${l.lng}`);
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent || "");
    let url;
    if (isIOS) {
      const origin = userLocation ? `${userLocation[0]},${userLocation[1]}` : stops[0];
      const daddr = (userLocation ? stops : stops.slice(1)).join("+to:");
      url = `http://maps.apple.com/?saddr=${origin}&daddr=${daddr}`;
    } else {
      const origin = userLocation ? `${userLocation[0]},${userLocation[1]}` : stops[0];
      const rest = userLocation ? stops : stops.slice(1);
      const destination = rest[rest.length - 1];
      const waypoints = rest.slice(0, -1);
      url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
      if (waypoints.length) url += `&waypoints=${encodeURIComponent(waypoints.join("|"))}`;
    }
    window.open(url, "_blank");
  };

  const sorted = useMemo(() => {
    const hiddenSet = new Set(user?.preferences?.hidden_listings || []);
    const vis = listings.filter((l) => !hiddenSet.has(l.id));
    if (!userLocation) return vis;
    if (sort === "nearest") {
      return [...vis].sort((a, b) => haversine(userLocation, [a.lat, a.lng]) - haversine(userLocation, [b.lat, b.lng]));
    }
    return [...vis].sort((a, b) => {
      if (!!b.highlighted !== !!a.highlighted) return (b.highlighted ? 1 : 0) - (a.highlighted ? 1 : 0);
      return haversine(userLocation, [a.lat, a.lng]) - haversine(userLocation, [b.lat, b.lng]);
    });
  }, [listings, userLocation, user, sort]);

  const topRatedIds = useMemo(() => {
    const rated = listings.filter((l) => (l.rating_count || 0) > 0);
    if (!rated.length) return new Set();
    const max = Math.max(...rated.map((l) => l.rating_avg || 0));
    if (max <= 0) return new Set();
    return new Set(rated.filter((l) => (l.rating_avg || 0) === max).map((l) => l.id));
  }, [listings]);

  return (
    <div className="h-screen flex flex-col bg-ys-canvas">
      <Header />
      {/* Filter bar */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          {/* Mobile: collapsible "Search here" bar + Map/List toggle */}
          <div className="sm:hidden">
            <div className="flex items-center gap-2">
              <button onClick={() => setSearchOpen((v) => !v)} data-testid="mobile-search-bar"
                className="flex-1 min-w-0 flex items-center gap-2 rounded-full h-11 border border-slate-200 bg-slate-50 px-4 text-left active:bg-slate-100">
                <Search className="w-4 h-4 text-slate-400 shrink-0" />
                <span className={`truncate text-sm ${filterSummary ? "text-slate-700 font-medium" : "text-slate-400"}`}>{filterSummary || "Search here — items, city, zip"}</span>
                <ChevronDown className={`w-4 h-4 ml-auto text-slate-400 shrink-0 transition-transform duration-200 ${searchOpen ? "rotate-180" : ""}`} />
              </button>
              <div className="flex rounded-full bg-slate-100 p-1 shrink-0" data-testid="mobile-view-toggle">
                <button onClick={() => setView("map")} data-testid="view-map" aria-label="Map view"
                  className={`flex items-center gap-1 px-3 h-9 rounded-full text-xs font-semibold transition-colors ${view !== "list" ? "bg-white shadow-sm text-ys-sky" : "text-slate-500"}`}><MapIcon className="w-4 h-4" />Map</button>
                <button onClick={() => setView("list")} data-testid="view-list" aria-label="Listings view"
                  className={`flex items-center gap-1 px-3 h-9 rounded-full text-xs font-semibold transition-colors ${view === "list" ? "bg-white shadow-sm text-ys-sky" : "text-slate-500"}`}><List className="w-4 h-4" />List</button>
              </div>
            </div>

            {searchOpen && (
              <div data-testid="mobile-search-panel" className="mt-3 rounded-2xl border border-slate-200 bg-white p-4 space-y-3 shadow-lg animate-in slide-in-from-top-2 fade-in duration-200">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }} placeholder="Items, tags, title…" data-testid="filter-q-input-m" className="pl-9 rounded-xl h-11" />
                </div>
                <div><span className="text-xs text-slate-500">City</span><Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" data-testid="filter-city-input-m" className="mt-1 rounded-xl h-11" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-xs text-slate-500">State</span><Input value={stateF} onChange={(e) => setStateF(e.target.value)} placeholder="State" data-testid="filter-state-input-m" className="mt-1 rounded-xl h-11" /></div>
                  <div><span className="text-xs text-slate-500">Zip</span><Input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="Zip" data-testid="filter-zip-input-m" className="mt-1 rounded-xl h-11" /></div>
                </div>
                <div><span className="text-xs text-slate-500">Sort by</span>
                  <Select value={sort} onValueChange={changeSort}>
                    <SelectTrigger className="mt-1 rounded-xl h-11" data-testid="sort-select-m"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recent">Newest</SelectItem>
                      <SelectItem value="nearest">Nearest first</SelectItem>
                      <SelectItem value="rating">Top rated</SelectItem>
                      <SelectItem value="city">City A-Z</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {tagOptions.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500 flex items-center gap-1"><Tag className="w-3.5 h-3.5" />Tags</span>
                      {tagFilter.length > 0 && <button onClick={() => setTagFilter([])} data-testid="tags-clear-mobile" className="text-xs font-semibold text-ys-sky">Clear</button>}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-1.5">
                      {tagOptions.map((t) => { const on = tagFilter.includes(t); return (
                        <button key={t} data-testid={`filter-tag-m-${t}`} onClick={() => toggleTag(t)}
                          className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? "bg-ys-sky text-white border-ys-sky" : "bg-white text-slate-500 border-slate-200"}`}>{t}</button>
                      ); })}
                    </div>
                  </div>
                )}
                <Button onClick={enableGps} variant={userLocation ? "secondary" : "outline"} data-testid="gps-toggle-btn-m" className="w-full rounded-full h-11 gap-1"><Navigation className="w-4 h-4" />{userLocation ? "Using your location" : "Use my location"}</Button>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" onClick={clearSearch} data-testid="mobile-search-clear" className="rounded-full h-11 flex-1">Clear all</Button>
                  <Button onClick={applySearch} data-testid="apply-filter-btn-m" className="rounded-full h-11 flex-[2] bg-ys-sky hover:bg-sky-700 gap-1"><Search className="w-4 h-4" />Search &amp; view map</Button>
                </div>
              </div>
            )}
          </div>

          {/* Desktop: inline filter row */}
          <div className="hidden sm:flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[160px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items, tags…" data-testid="filter-q-input" className="pl-9 rounded-full h-10" />
            </div>
            <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" data-testid="filter-city-input" className="w-28 rounded-full h-10" />
            <Input value={stateF} onChange={(e) => setStateF(e.target.value)} placeholder="State" data-testid="filter-state-input" className="w-24 rounded-full h-10" />
            <Input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="Zip" data-testid="filter-zip-input" className="w-24 rounded-full h-10" />
            <Button onClick={load} data-testid="apply-filter-btn" className="rounded-full h-10 bg-ys-sky hover:bg-sky-700 gap-1"><Search className="w-4 h-4" />Search</Button>
            <Select value={sort} onValueChange={changeSort}>
              <SelectTrigger className="w-36 rounded-full h-10" data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Newest</SelectItem>
                <SelectItem value="nearest">Nearest first</SelectItem>
                <SelectItem value="rating">Top rated</SelectItem>
                <SelectItem value="city">City A-Z</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={enableGps} variant={userLocation ? "secondary" : "outline"} data-testid="gps-toggle-btn" className="rounded-full h-10 gap-1"><Navigation className="w-4 h-4" />Near Me</Button>
            <div className="flex rounded-full bg-slate-100 p-1 shrink-0 ml-auto">
              <button onClick={() => setView("split")} data-testid="view-split" aria-label="Split view" className={`px-3 py-1.5 rounded-full ${view === "split" ? "bg-white shadow-sm" : ""}`}><MapIcon className="w-4 h-4" /></button>
              <button onClick={() => setView("list")} data-testid="view-list-d" aria-label="List view" className={`px-3 py-1.5 rounded-full ${view === "list" ? "bg-white shadow-sm" : ""}`}><List className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
        {tagOptions.length > 0 && (
          <div className="hidden sm:block max-w-7xl mx-auto px-4 sm:px-6 pb-3" data-testid="tag-filter-bar">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-mono uppercase tracking-widest text-slate-400 flex items-center gap-1"><Tag className="w-3.5 h-3.5" />Filter:</span>
              {tagOptions.map((t) => {
                const on = tagFilter.includes(t);
                return (
                  <button key={t} data-testid={`filter-tag-${t}`} onClick={() => toggleTag(t)}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? "bg-ys-sky text-white border-ys-sky" : "bg-white text-slate-500 border-slate-200 hover:border-ys-sky"}`}>{t}</button>
                );
              })}
            </div>
          </div>
        )}
        {route.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-3 flex items-center gap-2 flex-wrap" data-testid="route-planner-bar">
            <span className="text-sm font-semibold flex items-center gap-1"><Route className="w-4 h-4 text-ys-sky" />Your route ({orderedRoute.length}):</span>
            <Reorder.Group axis="x" values={orderedRoute} onReorder={(v) => { setRoute(v); setManualOrder(true); }} className="flex items-center gap-2 flex-wrap list-none m-0 p-0" data-testid="route-stops">
              {orderedRoute.map((l, i) => (
                <Reorder.Item key={l.id} value={l} className="inline-flex items-center gap-1 text-xs bg-sky-50 text-ys-sky rounded-full pl-1.5 pr-1 py-1 cursor-grab active:cursor-grabbing select-none" data-testid={`route-stop-${l.id}`}>
                  <GripVertical className="w-3 h-3 text-sky-400 shrink-0" />
                  <span>{i + 1}. {l.title}</span>
                  <button onPointerDown={(e) => e.stopPropagation()} onClick={() => toggleRoute(l)} data-testid={`route-remove-${l.id}`} aria-label="Remove stop" className="p-0.5 rounded-full hover:bg-sky-100"><X className="w-3 h-3" /></button>
                </Reorder.Item>
              ))}
            </Reorder.Group>
            {manualOrder && route.length > 1 && (
              <Button size="sm" variant="outline" className="rounded-full h-7 gap-1" onClick={() => setManualOrder(false)} data-testid="optimize-route-btn"><Navigation className="w-3 h-3" />Optimize</Button>
            )}
            {roadRoute && (
              <span className="text-xs font-semibold text-slate-600 bg-slate-100 rounded-full px-3 py-1" data-testid="route-summary">
                {(roadRoute.distance / 1609.34).toFixed(1)} mi · {Math.max(1, Math.round(roadRoute.duration / 60))} min drive
              </span>
            )}
            <Button size="sm" variant="outline" className="rounded-full h-7 gap-1" onClick={saveRoute} data-testid="save-route-btn"><Bookmark className="w-3 h-3" />Save</Button>
            <Button size="sm" className="rounded-full h-7 bg-ys-sky hover:bg-sky-700 gap-1" onClick={startNavigation} data-testid="start-navigation-btn" disabled={routePoints.length < 2}><Navigation className="w-3 h-3" />Start navigation</Button>
            <Button size="sm" variant="ghost" className="rounded-full h-7 text-red-500 gap-1" onClick={() => { setRoute([]); setManualOrder(false); }} data-testid="clear-route-btn"><Trash2 className="w-3 h-3" />Clear</Button>
          </div>
        )}
      </div>

      {promo?.active && (
        <div className="hidden" aria-hidden="true" />
      )}
      {promo?.ended && locked && (
        <div className="bg-slate-900 text-white px-4 py-2.5 flex items-center justify-center gap-3 flex-wrap text-sm" data-testid="promo-ended-bar">
          <span className="font-semibold">Our free promotion has ended — pick a plan to keep going.</span>
          <Button size="sm" onClick={() => navigate("/pricing")} data-testid="promo-ended-cta" className="rounded-full h-8 bg-ys-sky hover:bg-sky-700">See plans</Button>
        </div>
      )}
      {/* Preview banner for non-members */}
      {locked && !promo?.ended && (
        <div className="bg-sky-50 border-b border-sky-100 px-4 py-2.5 flex items-center justify-center gap-3 flex-wrap text-sm" data-testid="preview-banner">
          <span className="text-slate-600 flex items-center gap-1.5"><Lock className="w-4 h-4 text-ys-sky" />Preview mode — pick a plan to pan the map, zoom and open sales.</span>
          {user ? (
            <Button size="sm" onClick={() => navigate("/pricing")} data-testid="preview-plans-btn" className="rounded-full h-8 bg-ys-sky hover:bg-sky-700">See plans</Button>
          ) : (
            <span className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => navigate("/login")} data-testid="preview-signin-btn" className="rounded-full h-8">Sign in</Button>
              <Button size="sm" onClick={() => navigate("/register")} data-testid="preview-register-btn" className="rounded-full h-8 bg-ys-sky hover:bg-sky-700">Get started</Button>
            </span>
          )}
        </div>
      )}

      {/* Body */}
      <div className="flex-1 min-h-0 max-w-7xl w-full mx-auto flex relative">
        {view !== "list" && (
          <div className={`${view === "split" ? "hidden lg:block lg:w-3/5" : "w-full"} h-full`} data-testid="map-panel">
            <MapView listings={sorted} userLocation={userLocation} routeLine={routeLine} routeSolid={!!roadRoute} preview={locked} topRatedIds={[...topRatedIds]} fitTrigger={fitTrigger}
              className="h-full w-full" onSelect={(l) => (locked ? promptPlan() : setMarkerPreview(l))} />
          </div>
        )}
        {view !== "map" && (
          <div className={`${view === "split" ? "w-full lg:w-2/5" : "w-full"} h-full overflow-y-auto p-4`} data-testid="list-panel"
            style={{ touchAction: "pan-y" }}
            onTouchStart={(e) => { if (e.touches.length === 2) pinchRef.current = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY); }}
            onTouchMove={(e) => {
              if (e.touches.length !== 2) return;
              const dd = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
              const s = pinchRef.current;
              if (!s) { pinchRef.current = dd; return; }
              const ratio = dd / s;
              if (ratio > 1.3) { setCols((c) => Math.max(1, c - 1)); pinchRef.current = dd; }
              else if (ratio < 0.77) { setCols((c) => Math.min(4, c + 1)); pinchRef.current = dd; }
            }}
            onTouchEnd={(e) => { if (e.touches.length < 2) pinchRef.current = 0; }}>
            {loading ? (
              <div className="flex justify-center py-20"><div className="ys-spinner" /></div>
            ) : sorted.length === 0 ? (
              <div className="text-center py-20 text-slate-400">
                <MapPin className="w-10 h-10 mx-auto mb-2" />
                <p className="font-semibold">No current sales found</p>
                <p className="text-sm">Try clearing filters or check back soon.</p>
              </div>
            ) : (
              <>
                <p className="sm:hidden text-[11px] text-slate-400 mb-2" data-testid="pinch-hint">Pinch with two fingers to resize · {cols} column{cols === 1 ? "" : "s"}</p>
                <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }} data-testid="listings-grid">
                  {sorted.map((l) => (
                    <ListingCard key={l.id} l={l} onOpen={locked ? promptPlan : (x) => setSelected(x)} onRoute={locked ? promptPlan : toggleRoute} inRoute={!!route.find((r) => r.id === l.id)} onSave={locked ? promptPlan : toggleSave} onHide={locked ? promptPlan : toggleHide} saved={savedIds.has(l.id)} topRated={topRatedIds.has(l.id)} />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Map marker mini-preview */}      {markerPreview && (() => {
        const l = markerPreview;
        const cover = l.photos?.[l.profile_photo_index] || l.photos?.[0];
        const inRoute = !!route.find((r) => r.id === l.id);
        const dist = userLocation ? (haversine(userLocation, [l.lat, l.lng]) * 0.621371) : null;
        return (
          <div data-testid="marker-preview-card"
            className="fixed left-1/2 -translate-x-1/2 bottom-5 z-[1100] w-[calc(100vw-2rem)] max-w-sm rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-slate-900/25 overflow-hidden animate-in fade-in slide-in-from-bottom-3 duration-200">
            <button onClick={() => setMarkerPreview(null)} data-testid="marker-preview-close" aria-label="Close preview"
              className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-white/90 hover:bg-white shadow text-slate-500"><X className="w-4 h-4" /></button>
            <div className="flex gap-3 p-3">
              <div className="w-20 h-20 rounded-xl overflow-hidden bg-slate-100 shrink-0">
                {cover ? <img src={fileUrl(cover)} alt={l.title} className="w-full h-full object-cover" /> :
                  <div className="w-full h-full flex items-center justify-center text-slate-300"><MapPin className="w-6 h-6" /></div>}
              </div>
              <div className="min-w-0 flex-1 pr-5">
                {l.kind === "business" && (
                  <span data-testid="marker-preview-business-badge" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-white bg-indigo-700 rounded-full px-2 py-0.5 mb-1"><Briefcase className="w-3 h-3" />Business{l.business_type ? ` · ${l.business_type}` : ""}</span>
                )}
                <h3 className="font-display font-semibold text-sm leading-tight line-clamp-1">{l.title}</h3>
                <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5 truncate"><MapPin className="w-3 h-3 shrink-0" />{l.city}{l.state ? `, ${l.state}` : ""} {l.zip}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <div className="flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-ys-gold text-ys-gold" /><span className="text-xs font-semibold text-slate-600">{l.rating_avg || 0}</span><span className="text-[11px] text-slate-400">({l.rating_count || 0})</span></div>
                  {dist != null && <span data-testid="marker-preview-distance" className="text-[11px] font-semibold text-ys-sky bg-sky-50 rounded-full px-2 py-0.5">{dist < 10 ? dist.toFixed(1) : Math.round(dist)} mi</span>}
                </div>
              </div>
            </div>
            <div className="flex gap-2 px-3 pb-3">
              <Button size="sm" variant={inRoute ? "secondary" : "outline"} className="flex-1 rounded-full h-9 gap-1 text-xs" data-testid="marker-preview-route-btn"
                onClick={() => { toggleRoute(l); setMarkerPreview((p) => p ? { ...p } : p); }}>
                <Route className="w-3.5 h-3.5" />{inRoute ? "In route" : "Add to route"}
              </Button>
              {l.kind === "business" && l.contact_phone && (
                <a href={`tel:${l.contact_phone}`} data-testid="marker-preview-call-btn"
                  className="flex-1 inline-flex items-center justify-center gap-1 rounded-full h-9 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"><Phone className="w-3.5 h-3.5" />Call</a>
              )}
              <Button size="sm" className="flex-1 rounded-full h-9 bg-ys-sky hover:bg-sky-700 text-xs" data-testid="marker-preview-details-btn"
                onClick={() => { setSelected(l); setMarkerPreview(null); }}>View details</Button>
            </div>
          </div>
        );
      })()}

      {/* Selected sale sheet */}
      {view !== "list" && !markerPreview && sorted.length > 0 && (
        <button onClick={() => setFitTrigger((n) => n + 1)} data-testid="fit-all-btn"
          className="absolute top-3 right-3 z-[1001] rounded-full bg-white text-slate-700 shadow-lg border border-slate-200 h-10 px-4 flex items-center gap-1.5 text-xs font-semibold hover:bg-slate-50">
          <MapIcon className="w-4 h-4 text-ys-sky" />Fit all sales
        </button>
      )}
      {view === "map" && !markerPreview && (
        <button onClick={() => setListSheet(true)} data-testid="mobile-list-toggle"
          className="sm:hidden fixed bottom-24 left-1/2 -translate-x-1/2 z-[1001] rounded-full bg-ys-sky text-white shadow-xl shadow-sky-900/25 px-5 h-11 flex items-center gap-2 font-semibold text-sm">
          <List className="w-4 h-4" />Sales list ({sorted.length})
        </button>
      )}

      <Sheet open={listSheet} onOpenChange={setListSheet}>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[78vh] overflow-y-auto z-[1100]" data-testid="mobile-list-sheet">
          <SheetTitle className="text-left font-display">Sales on the map ({sorted.length})</SheetTitle>
          <div className="mt-3 space-y-2">
            {sorted.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No sales match your filters.</p>}
            {sorted.map((l) => (
              <button key={l.id} onClick={() => { setListSheet(false); locked ? promptPlan() : setSelected(l); }} data-testid={`mobile-list-item-${l.id}`}
                className="w-full text-left flex gap-3 items-center rounded-xl border border-slate-200 p-2.5 hover:border-ys-sky active:bg-slate-50">
                {l.photos?.[0] ? <img src={fileUrl(l.photos[l.profile_photo_index || 0] || l.photos[0])} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" /> : <div className="w-14 h-14 rounded-lg bg-slate-100 shrink-0 flex items-center justify-center"><MapPin className="w-5 h-5 text-slate-300" /></div>}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">{l.title}</p>
                  <p className="text-xs text-slate-500 truncate">{l.city} {l.zip}</p>
                  <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5"><Star className="w-3 h-3 fill-ys-gold text-ys-gold" />{l.rating_avg || 0} · {l.rating_count || 0}</div>
                </div>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[80vh] overflow-y-auto" data-testid="listing-preview-sheet">
          <SheetTitle className="sr-only">{selected ? selected.title : "Sale details"}</SheetTitle>
          {selected && (
            <div className="max-w-2xl mx-auto">
              <div className="h-48 rounded-2xl overflow-hidden bg-slate-100 mb-4">
                {selected.photos?.length ? <img src={fileUrl(selected.photos[selected.profile_photo_index] || selected.photos[0])} alt="" className="w-full h-full object-cover" /> :
                  <div className="w-full h-full flex items-center justify-center text-slate-300"><MapPin className="w-10 h-10" /></div>}
              </div>
              <div className="flex items-center justify-between gap-3">
                <h2 className="font-display text-xl font-bold">{selected.title}</h2>
                {selected.highlighted && <Badge className="bg-ys-gold text-white gap-1"><Sparkles className="w-3 h-3" />Highlighted</Badge>}
              </div>
              <p className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-4 h-4" />{selected.address}, {selected.city} {selected.zip}</p>
              <div className="flex items-center gap-2 mt-2"><StarRow value={selected.rating_avg} /><span className="text-sm text-slate-400">({selected.rating_count} ratings)</span></div>
              <p className="text-sm text-slate-600 mt-3 line-clamp-3">{selected.description}</p>
              <div className="flex gap-2 mt-5">
                <Button onClick={() => navigate(`/listing/${selected.id}`)} data-testid="open-listing-btn" className="flex-1 rounded-full bg-ys-sky hover:bg-sky-700">View Sale</Button>
                <Button variant="outline" onClick={() => { toggleRoute(selected); }} className="rounded-full gap-1" data-testid="sheet-route-btn"><Route className="w-4 h-4" />Route</Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
